/**
 * 経理「送金依頼」タブ（v3.x / 2026-07）。
 *
 * - 製品カテゴリー名のタブメニューで送金依頼中（requested）をグルーピング表示
 * - チェックボックスで選択 → 送金完了（メーカーへ画像添付メール）→ 完了マーク
 * - 「完了済み」トグルで履歴閲覧
 * - 2026-10-26: 右上「送金依頼を送信」で経理担当へまとめて連絡。連絡済みの行に「送金依頼済み」ラベル
 * - 2026-10-30: 表示切替 (?transferView=completed) と カテゴリー (?transferCat=) を URL に持たせ、
 *   「商談へ」は戻り先 (送金依頼) を渡す。商談詳細の「前の画面へ」で同じ表示に戻れる (チェックの選択は戻さない)
 */
import { formatMoney, isTransferSentToAccounting } from '@goenhub/shared';
import { doc, getDoc } from 'firebase/firestore';
import { getDownloadURL, ref as storageRef } from 'firebase/storage';
import { ExternalLink, FileText } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { SendTransferRequestsButton } from './SendTransferRequestsButton';
import { TransferCompletionDialog } from './TransferCompletionDialog';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useReturnToState } from '@/hooks/useReturnTo';
import { useTransferRequests, type TransferRequestRecord } from '@/hooks/useTransferRequests';
import { useUrlEnumParam, useUrlParam } from '@/hooks/useUrlParam';
import { db, storage } from '@/lib/firebase';
import { paths } from '@/routes/paths';

/** 送金依頼リストの行アクション: プロフォーマインボイス (メーカー見積) を開く / 商談の発注カードへ移動。 */
function RowActions({
  dealId,
  poId,
  manufacturerQuoteId,
}: {
  dealId: string;
  poId: string | null;
  manufacturerQuoteId: string | null;
}) {
  const navigate = useNavigate();
  const returnToState = useReturnToState('送金依頼');
  const [loading, setLoading] = useState(false);

  const openMakerQuote = async () => {
    if (!manufacturerQuoteId) {
      toast.error('プロフォーマインボイスが紐付いていません');
      return;
    }
    setLoading(true);
    try {
      const snap = await getDoc(
        doc(db, 'deals', dealId, 'manufacturer_quotes', manufacturerQuoteId),
      );
      const path = snap.exists()
        ? (snap.data() as { pdfStoragePath?: string }).pdfStoragePath
        : null;
      if (!path) {
        toast.error('プロフォーマインボイスの PDF が見つかりません');
        return;
      }
      const url = await getDownloadURL(storageRef(storage, path));
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      toast.error(`プロフォーマインボイスを開けません: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
      <Button
        variant="ghost"
        size="sm"
        onClick={openMakerQuote}
        disabled={loading || !manufacturerQuoteId}
        title="プロフォーマインボイスを新しいタブで開く"
      >
        <FileText className="size-3.5" />
        プロフォーマを開く
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() =>
          navigate(
            `${paths.dealDetail.replace(':id', dealId)}?tab=fulfillment${poId ? `&po=${poId}` : ''}`,
            { state: returnToState },
          )
        }
        title="この送金依頼の発注カードへ移動"
      >
        <ExternalLink className="size-3.5" />
        商談へ
      </Button>
    </div>
  );
}

function fmtDate(v: unknown): string {
  if (!v) return '-';
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    return (v as { toDate: () => Date }).toDate().toLocaleDateString('ja-JP');
  }
  const d = new Date(v as string);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString('ja-JP');
}

function fmtAmount(r: TransferRequestRecord): string {
  return formatMoney(r.finalAmount, r.currency);
}

const TRANSFER_VIEWS = ['requested', 'completed'] as const;

export function TransferRequestsPage() {
  const requested = useTransferRequests('requested');
  const completed = useTransferRequests('completed');

  const [view, setView] = useUrlEnumParam('transferView', TRANSFER_VIEWS, 'requested');
  const showCompleted = view === 'completed';
  const setShowCompleted = (v: boolean) => setView(v ? 'completed' : 'requested');
  const [urlCat, setActiveCat] = useUrlParam('transferCat'); // categoryId (無し = 先頭)
  const activeCat = urlCat ?? '';
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [completionOpen, setCompletionOpen] = useState(false);

  const rows = requested.data ?? [];

  // カテゴリー別グルーピング（送金依頼中）
  const categories = useMemo(() => {
    const map = new Map<string, { id: string; name: string; count: number }>();
    for (const r of rows) {
      const id = r.categoryId ?? '__none__';
      const name = r.categoryName ?? '（カテゴリー未設定）';
      const entry = map.get(id);
      if (entry) entry.count += 1;
      else map.set(id, { id, name, count: 1 });
    }
    return Array.from(map.values());
  }, [rows]);

  const effectiveCat =
    activeCat && categories.some((c) => c.id === activeCat) ? activeCat : (categories[0]?.id ?? '');

  const visibleRows = rows.filter((r) => (r.categoryId ?? '__none__') === effectiveCat);
  const selectedRows = visibleRows.filter((r) => selected.has(r.id));

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected((prev) => {
      const allSelected = visibleRows.every((r) => prev.has(r.id));
      const next = new Set(prev);
      if (allSelected) visibleRows.forEach((r) => next.delete(r.id));
      else visibleRows.forEach((r) => next.add(r.id));
      return next;
    });
  };

  const completedRows = completed.data ?? [];

  return (
    <Screen
      title="送金依頼"
      description="海外メーカーへの送金依頼を管理します。「送金依頼を送信」で送金依頼中のものを経理担当へまとめて連絡します。カテゴリー別に選択して「送金完了」を押すと、メーカーへ送金完了メール（証憑画像添付）を送信します。"
      actions={<SendTransferRequestsButton />}
      restoreScroll
    >
      {/* 表示切替 */}
      <div className="flex items-center gap-2 mb-4">
        <button
          type="button"
          onClick={() => setShowCompleted(false)}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
            !showCompleted
              ? 'bg-[var(--accent-subtle)] text-[var(--accent)] border-[var(--accent)]'
              : 'border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]'
          }`}
        >
          送金依頼中 {rows.length > 0 ? `(${rows.length})` : ''}
        </button>
        <button
          type="button"
          onClick={() => setShowCompleted(true)}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
            showCompleted
              ? 'bg-[var(--accent-subtle)] text-[var(--accent)] border-[var(--accent)]'
              : 'border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]'
          }`}
        >
          完了済み
        </button>
      </div>

      {showCompleted ? (
        /* ── 完了済み履歴 ── */
        completed.isLoading ? (
          <Spinner />
        ) : completedRows.length === 0 ? (
          <EmptyState title="完了済みの送金依頼はありません" />
        ) : (
          <div className="rounded-md border border-[var(--border)] overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-[var(--bg-elevated)] text-[var(--text-muted)]">
                <tr>
                  <th className="text-left p-2">完了日</th>
                  <th className="text-left p-2">カテゴリー</th>
                  <th className="text-left p-2">顧客名</th>
                  <th className="text-left p-2">発注商品</th>
                  <th className="text-left p-2">インボイス</th>
                  <th className="text-right p-2">金額</th>
                  <th className="text-right p-2">操作</th>
                </tr>
              </thead>
              <tbody>
                {completedRows.map((r) => (
                  <tr key={r.id} className="border-t border-[var(--border)]">
                    <td className="p-2">{fmtDate(r.completedAt)}</td>
                    <td className="p-2">{r.categoryName ?? '-'}</td>
                    <td className="p-2">
                      {r.customerName}
                      {r.customerNameEn ? ` / ${r.customerNameEn}` : ''}
                    </td>
                    <td className="p-2">{r.productName || '-'}</td>
                    <td className="p-2 font-mono">{r.invoiceNumber ?? '-'}</td>
                    <td className="p-2 text-right tabular-nums">{fmtAmount(r)}</td>
                    <td className="p-2">
                      <RowActions
                        dealId={r.dealId}
                        poId={r.poId}
                        manufacturerQuoteId={r.manufacturerQuoteId}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : /* ── 送金依頼中 ── */ requested.isLoading ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <EmptyState
          title="送金依頼はありません"
          description="商談詳細 > 発注 の「送金依頼追加」ボタンを押すと、ここに一覧表示されます。"
        />
      ) : (
        <>
          {/* カテゴリータブ */}
          <div className="flex flex-wrap gap-1.5 mb-3">
            {categories.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => {
                  setActiveCat(c.id);
                  setSelected(new Set());
                }}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${
                  effectiveCat === c.id
                    ? 'bg-[var(--accent)] text-white border-[var(--accent)]'
                    : 'border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]'
                }`}
              >
                {c.name} ({c.count})
              </button>
            ))}
          </div>

          {/* 選択サマリ + 送金完了 */}
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs text-[var(--text-muted)]">
              {selectedRows.length > 0
                ? `${selectedRows.length} 件選択中`
                : 'チェックボックスで送金完了する依頼を選択'}
            </div>
            <Button
              variant="primary"
              size="sm"
              disabled={selectedRows.length === 0}
              onClick={() => setCompletionOpen(true)}
            >
              送金完了（メーカーへ報告）
            </Button>
          </div>

          <div className="rounded-md border border-[var(--border)] overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-[var(--bg-elevated)] text-[var(--text-muted)]">
                <tr>
                  <th className="p-2 w-8">
                    <input
                      type="checkbox"
                      checked={
                        visibleRows.length > 0 && visibleRows.every((r) => selected.has(r.id))
                      }
                      onChange={toggleAll}
                    />
                  </th>
                  <th className="text-left p-2">依頼日</th>
                  <th className="text-left p-2">経理への連絡</th>
                  <th className="text-left p-2">顧客名</th>
                  <th className="text-left p-2">発注商品</th>
                  <th className="text-left p-2">インボイス</th>
                  <th className="text-right p-2">金額</th>
                  <th className="text-right p-2">操作</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((r) => (
                  <tr
                    key={r.id}
                    className="border-t border-[var(--border)] hover:bg-[var(--bg-hover)] cursor-pointer"
                    onClick={() => toggle(r.id)}
                  >
                    <td className="p-2" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selected.has(r.id)}
                        onChange={() => toggle(r.id)}
                      />
                    </td>
                    <td className="p-2">{fmtDate(r.requestedAt)}</td>
                    <td className="p-2">
                      {isTransferSentToAccounting(r) ? (
                        <Badge variant="approved">送金依頼済み</Badge>
                      ) : (
                        <span className="text-[var(--text-muted)]">未連絡</span>
                      )}
                    </td>
                    <td className="p-2">
                      {r.customerName}
                      {r.customerNameEn ? (
                        <span className="text-[var(--text-muted)]"> / {r.customerNameEn}</span>
                      ) : (
                        ''
                      )}
                    </td>
                    <td className="p-2">{r.productName || '-'}</td>
                    <td className="p-2 font-mono">{r.invoiceNumber ?? '-'}</td>
                    <td className="p-2 text-right tabular-nums font-semibold">{fmtAmount(r)}</td>
                    <td className="p-2">
                      <RowActions
                        dealId={r.dealId}
                        poId={r.poId}
                        manufacturerQuoteId={r.manufacturerQuoteId}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <TransferCompletionDialog
        open={completionOpen}
        onOpenChange={setCompletionOpen}
        requests={selectedRows}
        onCompleted={() => {
          setSelected(new Set());
          setCompletionOpen(false);
        }}
      />
    </Screen>
  );
}
